import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {AutocompleteProps, AutocompleteRenderOptionState, Box, TextFieldProps, Typography} from '@mui/material';
import Grid2 from '@mui/material/Unstable_Grid2';
import {CancelToken} from 'axios';
import {useTranslation} from 'react-i18next';
import useIsMounted from '../../../../hooks/use-is-mounted';
import AutocompleteBase from '../autocomplete-base';
import {mockWalletOptions} from './mock-wallets';

export interface WalletOption {
    id: string;
    name: string;
}

interface WalletAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<WalletOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;
    renderOption?: (
        props: React.HTMLAttributes<HTMLLIElement>,
        option: WalletOption,
        state: AutocompleteRenderOptionState,
        defaultRender: (props: React.HTMLAttributes<HTMLLIElement>, option: WalletOption) => React.ReactNode
    ) => React.ReactNode;

    [key: string]: any;
}

/**
 * Builds wallet options from mock data.
 * TODO: Replace with real wallet autocomplete API when endpoint is available.
 */
const buildMockWallets = (): WalletOption[] => mockWalletOptions.map((wallet) => ({...wallet}));

/**
 * Searchable wallet autocomplete (mock-backed for BizPoint Phase 1).
 */
const WalletAutocomplete = <M extends boolean = false, >({
                                                             textFieldProps = {},
                                                             form = false,
                                                             name = undefined,
                                                             required = false,
                                                             setLoadingOptions: setLoadingOptionsProps,
                                                             renderOption: customRenderOption,
                                                             ...props
                                                         }: WalletAutocompleteProps<M>) => {
    const {t} = useTranslation('components', {keyPrefix: 'app-specific.wallet-autocomplete'});
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);
    const allWallets = useMemo(() => buildMockWallets(), []);

    /**
     * Syncs the internal loading state with the parent component.
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Filters mock wallets by wallet id or customer name.
     */
    const getOptions = useCallback(async (_cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            await Promise.resolve();
            if (!isMounted()) {
                return [];
            }
            const query = (keywords ?? '').toString().trim().toLowerCase();
            if (!query) {
                return allWallets;
            }
            return allWallets.filter((wallet) =>
                wallet.id.toLowerCase().includes(query)
                || wallet.name.toLowerCase().includes(query),
            );
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [allWallets, isMounted]);

    const isOptionEqualToValue = useCallback(
        (option: WalletOption, value: WalletOption | null) => option.id === value?.id,
        [],
    );

    const getOptionLabel = useCallback(
        (option: WalletOption) => (option?.id ? `${option.name} (#${option.id})` : ''),
        [],
    );

    const getOptionKey = useCallback((option: WalletOption) => option?.id ?? '', []);

    const defaultRenderOption = useCallback((
        props: React.HTMLAttributes<HTMLLIElement> & { key?: string },
        option: WalletOption,
    ) => {
        const {key: _, ...otherProps} = props;
        return (
            <Box component="li" {...otherProps} sx={{display: 'flex', flexDirection: 'column', gap: 0.5}}>
                <Grid2 container alignItems="center" width="100%" spacing={1}>
                    <Grid2 xs={5}>
                        <Typography variant="caption" color="text.secondary">
                            {t(t => t.walletId)}
                        </Typography>
                    </Grid2>
                    <Grid2 xs={7}>
                        <Typography variant="caption" color="text.secondary">
                            {t(t => t.customerName)}
                        </Typography>
                    </Grid2>
                </Grid2>
                <Grid2 container alignItems="center" width="100%" spacing={1}>
                    <Grid2 xs={5}>
                        <Typography variant="body2" color="secondary.main">
                            {option.id}
                        </Typography>
                    </Grid2>
                    <Grid2 xs={7}>
                        <Typography variant="body2" color="secondary.main">
                            {option.name}
                        </Typography>
                    </Grid2>
                </Grid2>
            </Box>
        );
    }, [t]);

    const renderOption = useCallback((
        props: React.HTMLAttributes<HTMLLIElement>,
        option: WalletOption,
        state: AutocompleteRenderOptionState,
    ) => {
        if (customRenderOption) {
            return customRenderOption(props, option, state, defaultRenderOption);
        }
        return defaultRenderOption(props, option);
    }, [customRenderOption, defaultRenderOption]);

    const defaultOptions = useMemo(() => [], []);

    const mergedTextFieldProps = useMemo(() => ({
        label: t(t => t.label),
        ...textFieldProps,
    }), [t, textFieldProps]);

    return (
        <AutocompleteBase<WalletOption, M>
            canSearchEmptyKeywords
            keywordsBased
            getOptions={getOptions}
            timeout={300}
            textFieldProps={mergedTextFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            renderOption={renderOption}
            options={defaultOptions}
            clearOnBlur={false}
            noOptionsText={t(t => t.noOptions)}
            {...props}
        />
    );
};

export default WalletAutocomplete;

/**
 * Resolves a wallet form value (option object or string) to a wallet id.
 */
export const resolveWalletId = (wallet: WalletOption | string | null | undefined): string => {
    if (!wallet) {
        return '';
    }
    if (typeof wallet === 'string') {
        return wallet.trim();
    }
    return wallet.id?.toString()?.trim() ?? '';
};
